import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Category,
  createCategory,
  deleteCategory,
  getAdminContentsByCategory,
  listCategories,
  updateCategory,
  uploadCategoryPicture,
  deleteCategoryPicture,
} from '../../../../api/categories';
import { API_BASE, MEDIA_BASE } from '../../../../api/config';
import '../../../../assets/styles/categories-manager.scss';

type ContentItem = {
  id: number;
  title: string;
  type: string;
  is_visible: boolean;
  is_premium: boolean;
  created_at: string;
};

function resolveImageUrl(src?: string | null) {
  if (!src) return null;
  if (/^https?:\/\//i.test(src)) return src;
  const clean = src.replace(/^\/+/, '');
  return `${MEDIA_BASE}/media/${clean}`;
}

function pictureSrcFor(c: Category) {
  const base = c.picture_url || resolveImageUrl(c.picture);
  if (!base) return null;
  const v = encodeURIComponent(c.last_edited_at || c.created_at || '');
  return `${base}?v=${v}`;
}

export default function Categories() {
  const [items, setItems] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [newTitle, setNewTitle] = useState('');

  // akordeon (może być wiele otwartych)
  const [openSet, setOpenSet] = useState<Set<number>>(new Set());

  // per-kategoria treści i loading
  const [contentsByCat, setContentsByCat] = useState<Record<number, ContentItem[]>>({});
  const [loadingByCat, setLoadingByCat] = useState<Record<number, boolean>>({});

  // edycja nazwy (inline)
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const editInputRefs = useRef<Record<number, HTMLInputElement | null>>({});

  // ukryty input do uploadu obrazka
  const fileRef = useRef<HTMLInputElement | null>(null);
  const pendingPicForIdRef = useRef<number | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listCategories();
      setItems(list);
      // utrzymaj otwarte wiersze, jeśli jeszcze istnieją
      setOpenSet(prev => {
        const keep = new Set<number>();
        const ids = new Set(list.map(c => c.id));
        prev.forEach(id => { if (ids.has(id)) keep.add(id); });
        return keep;
      });
      // jeśli edytowałeś i kategoria zniknęła — wyczyść tryb edycji
      if (editingId && !list.find(c => c.id === editingId)) {
        setEditingId(null);
        setEditValue(''); 
      }
    } catch (e: any) {
      setError(e?.error || 'Nie udało się pobrać kategorii');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search) return items;
    const s = search.toLowerCase();
    return items.filter((c) =>
      c.title.toLowerCase().includes(s) || String(c.id).includes(s)
    );
  }, [items, search]);

  const onCreate = async () => {
    const title = newTitle.trim();
    if (!title) return;
    await createCategory(title, null);
    setNewTitle('');
    await load();
  };

  const onDelete = async (id: number) => {
    if (!window.confirm('Usunąć kategorię? Wszystkie treści zostaną przeniesione do "Uncategorized".')) return;
    await deleteCategory(id);
    setContentsByCat(prev => {
      const cp = { ...prev }; delete cp[id]; return cp;
    });
    setLoadingByCat(prev => {
      const cp = { ...prev }; delete cp[id]; return cp;
    });
    setOpenSet(prev => { const ns = new Set(prev); ns.delete(id); return ns; });
    if (editingId === id) { setEditingId(null); setEditValue(''); }
    await load();
  };

  const toggleOpen = async (cat: Category) => {
    const id = cat.id;
    setOpenSet(prev => {
      const ns = new Set(prev);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });

    if (!contentsByCat[id]) {
      setLoadingByCat(prev => ({ ...prev, [id]: true }));
      try {
        const { items: list } = await getAdminContentsByCategory(id);
        setContentsByCat(prev => ({ ...prev, [id]: list || [] }));
      } catch {
        setContentsByCat(prev => ({ ...prev, [id]: [] }));
      } finally {
        setLoadingByCat(prev => ({ ...prev, [id]: false }));
      }
    }
  };

  const pickPicture = (id: number) => {
    pendingPicForIdRef.current = id;
    if (fileRef.current) fileRef.current.value = '';
    fileRef.current?.click();
  };

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = pendingPicForIdRef.current;
    pendingPicForIdRef.current = null;
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!id || !f) return;
    await uploadCategoryPicture(id, f);
    await load();
  };

  const onRemovePicture = async (id: number) => {
    await deleteCategoryPicture(id);
    await load();
  };

  // start edycji
  const startEdit = (c: Category) => {
    setEditingId(c.id);
    setEditValue(c.title);
    // mała zwłoka, by ref już istniał, potem focus
    setTimeout(() => {
      editInputRefs.current[c.id]?.focus();
      editInputRefs.current[c.id]?.select();
    }, 0);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const saveEdit = async (id: number) => {
    const val = editValue.trim();
    if (!val) return;
    setSavingId(id);
    try {
      await updateCategory(id, { title: val });
      setEditingId(null);
      setEditValue('');
      await load();
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="catm-box">
      <div className="catm-header">
        <h2>Kategorie</h2>
        <div className="catm-search">
          <input placeholder="Szukaj po ID / nazwie…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {error && <div className="catm-error">{error}</div>}
      {loading && <div className="catm-loading">Ładowanie…</div>}

      <div className="catm-new">
        <input
          placeholder="Nazwa nowej kategorii"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <button className='btn-primary' onClick={onCreate}>Dodaj</button>
      </div>

      {/* ukryty input do uploadu */}
      <input type="file" ref={fileRef} accept="image/*" style={{ display: 'none' }} onChange={onFilePicked} />

      <ul className="catm-accordion">
        {filtered.map((c) => {
          const open = openSet.has(c.id);
          const img = pictureSrcFor(c);
          const list = contentsByCat[c.id] || [];
          const isLoadingList = !!loadingByCat[c.id];
          const isEditing = editingId === c.id;

          return (
            <li key={c.id} className={`catm-row ${open ? 'open' : ''}`}>
              <div className="catm-row-main">
                {/* miniatura */}
                <div className="catm-thumb-small">
                  {img ? <img src={img} alt={c.title} /> : <div className="catm-thumb-ph" aria-hidden />}
                </div>

                {/* tytuł / edycja */}
                <div className="catm-row-meta">
                  {!isEditing ? (
                    <>
                      <div className="catm-row-title">
                        <span className="catm-id">#{c.id}</span>
                        <span className="catm-name">{c.title}</span>
                      </div>
                      <div className="catm-row-sub">Utworzono: {new Date(c.created_at!).toLocaleString()}</div>
                    </>
                  ) : (
                    <div className="catm-edit-inline">
                      <input
                      //  ref={(el) => (editInputRefs.current[c.id] = el)}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        placeholder="Nazwa kategorii"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') saveEdit(c.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                      />
                      <div className="catm-edit-inline-actions">
                        <button className="btn" onClick={() => saveEdit(c.id)} disabled={savingId === c.id || !editValue.trim()}>
                          {savingId === c.id ? 'Zapisywanie…' : 'Zapisz'}
                        </button>
                        <button className="btn ghost" onClick={cancelEdit}>Anuluj</button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="catm-row-actions">
                  <button
                    className="btn small ghost"
                    onClick={() => pickPicture(c.id)}
                    title={img ? 'Zmień obrazek' : 'Dodaj obrazek'}
                  >
                    {img ? 'Zmień obrazek' : 'Dodaj obrazek'}
                  </button>

                  {/* obrazek: usuń */}
                  {img && (
                    <button
                      className="btn small ghost danger-outline"
                      onClick={() => onRemovePicture(c.id)}
                      title="Usuń obrazek"
                    >
                      Usuń obrazek
                    </button>
                  )}

                  {/* rozwiń/zwiń contents */}
                  <button
                    className="btn small ghost"
                    onClick={() => toggleOpen(c)}
                    aria-expanded={open}
                    aria-controls={`cat-panel-${c.id}`}
                    title={open ? 'Zwiń treści' : 'Pokaż treści'}
                  >
                    {open ? 'Zwiń treści' : 'Pokaż treści'}
                  </button>
                  
                                    {!isEditing ? (
                    <button
                      className="icon-btn edit"
                      onClick={() => startEdit(c)}
                      aria-label="Edytuj"
                      title="Edytuj nazwę"
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 20h9"></path>
                        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"></path>
                      </svg>
                    </button>
                  ) : ( <></> )}

                  {/* Usuń kategorię */}
                  <button
                    className="icon-btn delete"
                    onClick={() => onDelete(c.id)}
                    aria-label="Usuń"
                    title="Usuń kategorię"
                  >
                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18"></path>
                      <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                    </svg>
                  </button>
                </div>
              </div>

              {/* panel treści */}
              <div id={`cat-panel-${c.id}`} className="catm-row-panel" style={{ display: open ? 'block' : 'none' }}>
                {isLoadingList ? (
                  <div className="catm-loading">Ładowanie treści…</div>
                ) : (
                  <div className="catm-table-wrap">
                    <table className="catm-table">
                      <thead>
                        <tr>
                          <th>ID</th><th>Tytuł</th><th>Typ</th><th>Widoczność</th><th>Premium</th><th>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {list.map((ci) => (
                          <tr key={ci.id}>
                            <td>{ci.id}</td>
                            <td>{ci.title}</td>
                            <td>{ci.type}</td>
                            <td className="strong">
                              <span className={`badge ${ci.is_visible ? 'badge-success' : 'badge-muted'}`}>{ci.is_visible ? 'TAK' : 'NIE'}</span>
                            </td>
                            <td className="strong">
                              <span className={`badge ${ci.is_premium ? 'badge-success' : 'badge-muted'}`}>{ci.is_premium ? 'TAK' : 'NIE'}</span>
                            </td>
                            <td>{new Date(ci.created_at).toLocaleString()}</td>
                          </tr>
                        ))}
                        {!list.length && (
                          <tr>
                            <td colSpan={6} style={{ textAlign: 'center', opacity: 0.7 }}>Brak treści w tej kategorii</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </li>
          );
        })}
        {!filtered.length && <li className="empty">Brak kategorii</li>}
      </ul>
    </div>
  );
}
